import { DeleteOutlined } from '@ant-design/icons';
import { Button, Card, Col, Image, message, Row, Typography } from 'antd';
import useModal from 'antd/lib/modal/useModal';
import { colors, toRgba } from 'common/styles/colors';
import { AttachmentUpload } from 'components/molecules/AttachmentUpload';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { useDeleteAttachmentMutation, useGetSalesOrderQuery, usePostAttachmentMutation } from 'redux/services/useMamaBearService';

export const AttachmentsStep = (): JSX.Element => {
  const [modal, context] = useModal();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { id } = useParams();
  const { data: salesOrder } = useGetSalesOrderQuery({ id: id as string });
  const [deleteAttachment] = useDeleteAttachmentMutation();
  const [postAttachment] = usePostAttachmentMutation();
  const isCreate = pathname.includes('create');
  const isPosted = pathname.includes('posted');
  const handleOk = (fileName: string): void => {
    try {
      deleteAttachment({ salesOrderId: id as string, fileName: fileName });
      message.success('Successfully deleted attachment.');
    } catch (e) {
      message.error((e as { data: string }).data ?? `Failed to delete attachment: ${fileName}.`);
      console.log(e);
    }
  };

  return (
    <Row gutter={8}>
      <Col span={isCreate ? 24 : 12} style={{ display: isPosted ? 'none' : undefined }}>
        <Card
          headStyle={{ backgroundColor: toRgba(colors.romanSilver, 0.4) }}
          title={
            <Typography.Title level={4} style={{ fontWeight: 'normal' }}>
              Add File Attachments
            </Typography.Title>
          }>
          <Row style={{ marginTop: 5 }}>
            <Col span={24} style={{ height: '100%' }}>
              <AttachmentUpload />
            </Col>
          </Row>
        </Card>
      </Col>
      <Col span={isPosted ? 24 : 12} style={{ marginBottom: 50, display: isCreate ? 'none' : undefined }}>
        <Card
          headStyle={{ backgroundColor: toRgba(colors.romanSilver, 0.4) }}
          bodyStyle={{ minHeight: 259 }}
          title={
            <Typography.Title level={4} style={{ fontWeight: 'normal' }}>
              {isPosted ? 'Posted File Attachments' : 'Existing File Attachments'}
            </Typography.Title>
          }>
          <Row style={{ marginTop: 5 }}>
            <Col span={24} style={{ height: '100%' }}>
              {context}
              {salesOrder?.attachments?.map((attachment) => (
                <Card bodyStyle={{ padding: 8 }} key={attachment.name} style={{ marginBottom: 5 }}>
                  <Row align="middle" justify="space-between">
                    <Col>
                      <Image
                        fallback={
                          'data:image/svg+xml;base64,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'
                        }
                        width={48}
                        height={48}
                        src={(attachment as unknown as { url: string }).url}
                      />
                      <Typography.Link href={(attachment as unknown as { url: string }).url} style={{ marginLeft: 15 }}>
                        {attachment.name}
                      </Typography.Link>
                    </Col>
                    {!isPosted ? (
                      <DeleteOutlined
                        onClick={(): void => {
                          modal.confirm({
                            content: 'Are you sure you want to delete this attachment?',
                            onOk: (): void => handleOk(attachment.name)
                          });
                        }}
                      />
                    ) : (
                      <Button
                        style={{ display: salesOrder.attachments?.find((item) => item.name === attachment.name)?.submittedToD365 ? 'none' : undefined }}
                        onClick={() => postAttachment({ salesOrderId: id as string, fileName: attachment.name })}>
                        Post to D365
                      </Button>
                    )}
                  </Row>
                </Card>
              ))}
            </Col>
          </Row>
        </Card>
      </Col>
    </Row>
  );
};
